/*
 * Логика отказа от блюда — чистые функции без экрана и сети.
 *
 * Всё, что можно посчитать и проверить тестом, живёт здесь: новое
 * состояние чека, событие для отчётов, текст подтверждения. Экран
 * (VoidFlow) только водит человека по шагам и вызывает это.
 */
import { reduceOrder, ulid, type OrderState } from '@dastarhan/core';
import type { SyncEvent } from '@dastarhan/contracts';

export type VoidResult = {
  /** чек после снятия */
  after: OrderState;
  /** событие для очереди синка — отчёты, лента Z, акт списания */
  event: SyncEvent;
  /** текст для кассира */
  toast: string;
};

export function applyVoid(args: {
  order: OrderState;
  itemId: string;
  qty: number;
  reason: SyncEvent extends { reason: infer R } ? R : string;
  comment: string;
  terminalId: string;
  /** имя одобрившего старшего — попадает в комментарий события */
  approver?: string | null;
  /** связь была недоступна, PIN старшего узнан локально либо пропущен */
  offlineNote?: string | null;
}): VoidResult | null {
  const it = args.order.items.find((x) => x.itemId === args.itemId && !x.removed);
  if (!it) return null;
  const qty = Math.min(Math.max(args.qty, 0), it.qty);
  if (qty <= 0) return null;

  const after = qty >= it.qty
    ? reduceOrder(args.order, { type: 'item.removed', itemId: it.itemId })
    : reduceOrder(args.order, { type: 'item.qty', itemId: it.itemId, qty: it.qty - qty });

  // Комментарий события собирается из трёх честных кусков:
  // слова кассира · кто одобрил · как была проверена связь
  const commentParts = [
    args.comment.trim(),
    args.approver ? `одобрил: ${args.approver}` : '',
    args.offlineNote ?? '',
  ].filter(Boolean);

  const event = {
    eventId: ulid(),
    terminalId: args.terminalId,
    at: new Date().toISOString(),
    type: 'item.voided',
    orderId: args.order.orderId,
    itemId: it.itemId,
    name: qty < it.qty ? `${it.name} (${qty} из ${it.qty})` : it.name,
    qty,
    sum: Math.round(qty * (it.unitPrice
      + it.modifiers.reduce((a, m) => a + m.priceDelta, 0))),
    reason: args.reason,
    ...(commentParts.length ? { comment: commentParts.join(' · ').slice(0, 200) } : {}),
  } as SyncEvent;

  return {
    after,
    event,
    toast: qty < it.qty ? `Убрано ${qty} шт.` : 'Позиция снята',
  };
}
